import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { PerformanceData } from '@/hooks/usePerformanceData';

interface PerformanceComparisonProps {
  data: PerformanceData | null;
}

export function PerformanceComparison({ data }: PerformanceComparisonProps) {
  if (!data) return null;

  const activeRecruiters = data.recruiters.filter((r) => r.totalCandidates > 0);

  if (activeRecruiters.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Performance Comparison</CardTitle>
          <CardDescription>Compare recruiters against team averages</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center text-muted-foreground">
            No data available for comparison
          </div>
        </CardContent>
      </Card>
    );
  }

  // Prepare radar chart data for team comparison
  const radarData = [
    {
      metric: 'Interview Rate',
      teamAverage: data.teamAverages.interviewRate,
      ...Object.fromEntries(
        activeRecruiters.slice(0, 5).map((r) => [r.recruiterName.split(' ')[0], r.interviewRate])
      ),
    },
    {
      metric: 'Selection Rate',
      teamAverage: data.teamAverages.selectionRate,
      ...Object.fromEntries(
        activeRecruiters.slice(0, 5).map((r) => [r.recruiterName.split(' ')[0], r.selectionRate])
      ),
    },
    {
      metric: 'Conversion Rate',
      teamAverage: data.teamAverages.conversionRate,
      ...Object.fromEntries(
        activeRecruiters.slice(0, 5).map((r) => [r.recruiterName.split(' ')[0], r.conversionRate])
      ),
    },
    {
      metric: 'Candidates/Position',
      teamAverage: data.teamAverages.openPositions > 0 
        ? data.teamAverages.totalCandidates / data.teamAverages.openPositions 
        : 0,
      ...Object.fromEntries(
        activeRecruiters.slice(0, 5).map((r) => [
          r.recruiterName.split(' ')[0], 
          r.openPositions > 0 ? r.totalCandidates / r.openPositions : 0
        ])
      ),
    },
  ];

  // Prepare leaderboard data
  const leaderboard = [...activeRecruiters]
    .sort((a, b) => b.conversionRate - a.conversionRate)
    .slice(0, 5);

  const colors = [
    'hsl(239 84% 67%)',
    'hsl(142 76% 36%)',
    'hsl(45 93% 47%)',
    'hsl(0 84% 60%)',
    'hsl(280 65% 60%)',
  ];

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {/* Radar Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Performance Radar</CardTitle>
          <CardDescription>Multi-metric comparison across recruiters</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11 }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} />
                <Radar
                  name="Team Average"
                  dataKey="teamAverage"
                  stroke="hsl(var(--muted-foreground))"
                  fill="hsl(var(--muted-foreground))"
                  fillOpacity={0.2}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                />
                {activeRecruiters.slice(0, 3).map((recruiter, index) => (
                  <Radar
                    key={recruiter.recruiterId}
                    name={recruiter.recruiterName.split(' ')[0]}
                    dataKey={recruiter.recruiterName.split(' ')[0]}
                    stroke={colors[index]}
                    fill={colors[index]}
                    fillOpacity={0.1}
                    strokeWidth={2}
                  />
                ))}
                <Legend />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Leaderboard */}
      <Card>
        <CardHeader>
          <CardTitle>Top Performers</CardTitle>
          <CardDescription>Ranked by conversion rate</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {leaderboard.map((recruiter, index) => (
              <div
                key={recruiter.recruiterId}
                className="flex items-center gap-4 p-3 rounded-lg bg-muted/50"
              >
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                    index === 0
                      ? 'bg-yellow-500/20 text-yellow-600'
                      : index === 1
                      ? 'bg-gray-400/20 text-gray-600'
                      : index === 2
                      ? 'bg-amber-600/20 text-amber-700'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  #{index + 1}
                </div>
                <div className="flex-1">
                  <div className="font-medium">{recruiter.recruiterName}</div>
                  <div className="text-sm text-muted-foreground">
                    {recruiter.candidatesSelected} selected • {recruiter.candidatesInterviewed} interviewed
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-primary">
                    {recruiter.conversionRate.toFixed(1)}%
                  </div>
                  <div className="text-xs text-muted-foreground">conversion</div>
                </div>
              </div>
            ))}
            {leaderboard.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No performance data available
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
